import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { DBPosition } from "../api/types";
import { WithId } from "mongodb";
import { useAPI } from "../api/useAPI";
import { useRefetchLoop } from "../hooks/useRefetchLoop";

export type FilterTimeRange = "all" | "7" | "14" | "30" | "90";

export type PositionsContextState = {
  positions: WithId<DBPosition>[];
  filteredPositions: WithId<DBPosition>[];
  // Start of the selected time range, undefined for "all"
  rangeStartTs?: number;
  // Whether the account already traded before the range start
  hasTradesBeforeRange: boolean;
  timeRange: FilterTimeRange;
  setTimeRange: (range: FilterTimeRange) => void;
  accountId?: string;
  setAccountId: (id: string) => void;
};

const initialPositionsContext: PositionsContextState = {
  positions: [],
  filteredPositions: [],
  hasTradesBeforeRange: false,
  timeRange: "all",
  setTimeRange: () => {},
  accountId: undefined,
  setAccountId: () => {},
};

const PositionsContext = createContext(initialPositionsContext);

export const usePositionsContext = () => useContext(PositionsContext);

export default function PositionsContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [positions, setPositions] = useState<WithId<DBPosition>[]>([]);
  const [filteredPositions, setFilteredPositions] = useState<
    WithId<DBPosition>[]
  >([]);
  const [rangeStartTs, setRangeStartTs] = useState<number>();
  const [hasTradesBeforeRange, setHasTradesBeforeRange] = useState(false);
  const [timeRange, setTimeRange] = useState<FilterTimeRange>(() => {
    const saved = localStorage.getItem("positions-time-range");
    return (saved as FilterTimeRange) || "all";
  });
  const [accountId, setAccountId] = useState<string>();
  const { getPositions } = useAPI();

  // Save timeRange to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("positions-time-range", timeRange);
  }, [timeRange]);

  const fetchAccountPositions = useCallback(() => {
    if (accountId) {
      getPositions(accountId).then((response) => {
        console.log("🚀 ~ PositionsContextProvider ~ response:", response);
        if (response.success) {
          const startTs = new Date("Tue, 22 Jul 2025 18:46:52 GMT").getTime();
          const fetchedPositions = response.data.filter(
            (position) =>
              new Date(position.openPositionTimestamp ?? 0).getTime() > startTs,
          );

          fetchedPositions.sort((a, b) => {
            return (
              new Date(b.openPositionTimestamp ?? 0).getTime() -
              new Date(a.openPositionTimestamp ?? 0).getTime()
            );
          });

          setPositions(fetchedPositions);
        }
      });
    } else {
      setPositions([]);
    }
  }, [accountId]);

  // Fetch positions when account ID changes
  useEffect(() => {
    fetchAccountPositions();
  }, [accountId]);

  useRefetchLoop(fetchAccountPositions, 60);

  // Filter positions when timeRange or positions change
  useEffect(() => {
    const now = new Date();
    const filterPositions = () => {
      if (timeRange === "all") {
        setFilteredPositions(positions);
        setRangeStartTs(undefined);
        setHasTradesBeforeRange(false);
        return;
      }

      const days = parseInt(timeRange);

      const cutoffDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);

      const filtered = positions.filter((position) => {
        const positionDate = position.openPositionTimestamp
          ? new Date(position.openPositionTimestamp)
          : new Date(0);
        return positionDate >= cutoffDate;
      });

      setFilteredPositions(filtered);
      setRangeStartTs(cutoffDate.getTime());
      setHasTradesBeforeRange(filtered.length < positions.length);
    };

    filterPositions();
  }, [timeRange, positions]);

  return (
    <PositionsContext.Provider
      value={{
        positions,
        filteredPositions,
        rangeStartTs,
        hasTradesBeforeRange,
        timeRange,
        setTimeRange,
        accountId,
        setAccountId,
      }}
    >
      {children}
    </PositionsContext.Provider>
  );
}
